if (!customElements.get('product-form')) {
  customElements.define(
    'product-form',
    class ProductForm extends HTMLElement {
      constructor() {
        super();

        this.form = this.querySelector('form');
        this.variantIdInput.disabled = false;
        this.form.addEventListener('submit', this.onSubmitHandler.bind(this));
        this.cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
        this.submitButton = this.querySelector('[type="submit"]');
        this.submitButtonText = this.submitButton.querySelector('span');

        if (document.querySelector('cart-drawer')) this.submitButton.setAttribute('aria-haspopup', 'dialog');

        this.hideErrors = this.dataset.hideErrors === 'true';
        // TL: Dynamic checkout bypasses bundled add-on adds, so hide it when add-ons are selected.
        this.dynamicCheckoutElement = this.querySelector('.shopify-payment-button');
        this.addOnInputs = [];
        this.bindAddOnListeners();
        this.setupDynamicCheckoutNotice();
        this.syncDynamicCheckoutState();
      }

      // TL: Collect checked add-on variant IDs rendered by the Tinylabs product add-ons block.
      getSelectedAddOnVariantIds() {
        const productInfo = this.closest('product-info');
        if (!productInfo) return [];

        const selectedInputs = productInfo.querySelectorAll('[data-tl-addon-variant-id]:checked');
        return [...selectedInputs]
          .map((input) => parseInt(input.value, 10))
          .filter((value) => Number.isInteger(value) && value > 0);
      }

      // TL: Subscribe to add-on checkbox changes so dynamic checkout visibility stays accurate.
      bindAddOnListeners() {
        const productInfo = this.closest('product-info');
        if (!productInfo) return;
        this.addOnInputs = [...productInfo.querySelectorAll('[data-tl-addon-variant-id]')];
        this.addOnInputs.forEach((input) => input.addEventListener('change', () => this.syncDynamicCheckoutState()));
      }

      // TL: Display a concise explanation when dynamic checkout is hidden due to selected add-ons.
      setupDynamicCheckoutNotice() {
        if (!this.dynamicCheckoutElement) return;
        this.dynamicCheckoutNotice = document.createElement('p');
        this.dynamicCheckoutNotice.className = 'tl-dynamic-checkout-note';
        this.dynamicCheckoutNotice.textContent = 'To include selected add-ons, use Add to cart.';
        this.dynamicCheckoutNotice.hidden = true;
        this.dynamicCheckoutElement.insertAdjacentElement('afterend', this.dynamicCheckoutNotice);
      }

      // TL: Keep "Buy it now" available only when no add-ons are selected.
      syncDynamicCheckoutState() {
        if (!this.dynamicCheckoutElement) return;
        const hasSelectedAddOns = this.getSelectedAddOnVariantIds().length > 0;
        this.dynamicCheckoutElement.classList.toggle('tl-hidden-for-addons', hasSelectedAddOns);
        if (this.dynamicCheckoutNotice) this.dynamicCheckoutNotice.hidden = !hasSelectedAddOns;
      }

      // TL: Build bundled cart payload so main product and selected add-ons are added in one request.
      getBundledItemsPayload(formData, addOnVariantIds) {
        const mainVariantId = parseInt(formData.get('id'), 10);
        const mainQuantity = parseInt(formData.get('quantity'), 10) || 1;
        const selectedIds = new Set([mainVariantId, ...addOnVariantIds]);
        const items = [];
        const properties = {};

        for (const [key, value] of formData.entries()) {
          if (!key.startsWith('properties[') || !key.endsWith(']')) continue;
          properties[key.slice(11, -1)] = value;
        }

        selectedIds.forEach((variantId) => {
          const item = {
            id: variantId,
            quantity: variantId === mainVariantId ? mainQuantity : 1,
          };

          if (variantId === mainVariantId) {
            const sellingPlan = formData.get('selling_plan');
            if (sellingPlan) item.selling_plan = sellingPlan;
            if (Object.keys(properties).length) item.properties = properties;
          }

          items.push(item);
        });

        return {
          mainVariantId,
          items,
        };
      }

      // TL: Ensure bundled add responses include the fields Dawn cart UI expects.
      async normalizeBundledAddResponse(response) {
        if (!this.cart) return response;

        // TL: Preserve all added line-item keys so cart notification can render each added item.
        if (!Array.isArray(response.addedItemKeys) && Array.isArray(response.items)) {
          response.addedItemKeys = response.items
            .map((item) => item?.key)
            .filter((itemKey) => typeof itemKey === 'string' && itemKey.length > 0);
        }

        if (!response.key && Array.isArray(response.items) && response.items[0]?.key) {
          response.key = response.items[0].key;
        }

        const sectionIds = this.cart.getSectionsToRender().map((section) => section.id);
        const hasAllSections =
          response.sections &&
          sectionIds.length > 0 &&
          sectionIds.every((sectionId) => typeof response.sections[sectionId] === 'string');

        if (hasAllSections) return response;

        const sectionsResponse = await fetch(
          `${window.location.pathname}?sections=${encodeURIComponent(sectionIds.join(','))}`
        );
        response.sections = await sectionsResponse.json();
        return response;
      }

      onSubmitHandler(evt) {
        evt.preventDefault();
        if (this.submitButton.getAttribute('aria-disabled') === 'true') return;

        this.handleErrorMessage();

        this.submitButton.setAttribute('aria-disabled', true);
        this.submitButton.classList.add('loading');
        this.querySelector('.loading__spinner').classList.remove('hidden');

        const config = fetchConfig('javascript');
        config.headers['X-Requested-With'] = 'XMLHttpRequest';

        const formData = new FormData(this.form);
        const selectedAddOnVariantIds = this.getSelectedAddOnVariantIds();
        const isBundledAdd = selectedAddOnVariantIds.length > 0;
        let selectedMainVariantId = formData.get('id');

        if (isBundledAdd) {
          // TL: Use JSON payload with `items` for bundled adds when add-ons are selected.
          const bundledItemsPayload = this.getBundledItemsPayload(formData, selectedAddOnVariantIds);
          selectedMainVariantId = bundledItemsPayload.mainVariantId;
          const payload = {
            items: bundledItemsPayload.items,
          };
          if (this.cart) {
            // TL: Match Dawn's default form payload format; sections must be a comma-separated string.
            payload.sections = this.cart
              .getSectionsToRender()
              .map((section) => section.id)
              .join(',');
            payload.sections_url = window.location.pathname;
            this.cart.setActiveElement(document.activeElement);
          }
          config.body = JSON.stringify(payload);
        } else {
          delete config.headers['Content-Type'];
          if (this.cart) {
            formData.append(
              'sections',
              this.cart.getSectionsToRender().map((section) => section.id)
            );
            formData.append('sections_url', window.location.pathname);
            this.cart.setActiveElement(document.activeElement);
          }
          config.body = formData;
        }


        fetch(`${routes.cart_add_url}`, config)
          .then((response) => response.json())
          .then(async (response) => {
            if (response.status) {
              publish(PUB_SUB_EVENTS.cartError, {
                source: 'product-form',
                productVariantId: selectedMainVariantId,
                errors: response.errors || response.description,
                message: response.message,
              });
              this.handleErrorMessage(response.description);

              const soldOutMessage = this.submitButton.querySelector('.sold-out-message');
              if (!soldOutMessage) return;
              this.submitButton.setAttribute('aria-disabled', true);
              this.submitButtonText.classList.add('hidden');
              soldOutMessage.classList.remove('hidden');
              this.error = true;
              return;
            } else if (!this.cart) {
              window.location = window.routes.cart_url;
              return;
            }

            if (isBundledAdd) {
              response = await this.normalizeBundledAddResponse(response);
            }

            const startMarker = CartPerformance.createStartingMarker('add:wait-for-subscribers');
            if (!this.error)
              publish(PUB_SUB_EVENTS.cartUpdate, {
                source: 'product-form',
                productVariantId: selectedMainVariantId,
                cartData: response,
              }).then(() => {
                CartPerformance.measureFromMarker('add:wait-for-subscribers', startMarker);
              });
            this.error = false;
            const quickAddModal = this.closest('quick-add-modal');
            if (quickAddModal) {
              document.body.addEventListener(
                'modalClosed',
                () => {
                  setTimeout(() => {
                    CartPerformance.measure("add:paint-updated-sections", () => {
                      this.cart.renderContents(response);
                    });
                  });
                },
                { once: true }
              );
              quickAddModal.hide(true);
            } else {
              CartPerformance.measure("add:paint-updated-sections", () => {
                this.cart.renderContents(response);
              });
            }
          })
          .catch((e) => {
            console.error(e);
          })
          .finally(() => {
            this.submitButton.classList.remove('loading');
            if (this.cart && this.cart.classList.contains('is-empty')) this.cart.classList.remove('is-empty');
            if (!this.error) this.submitButton.removeAttribute('aria-disabled');
            this.querySelector('.loading__spinner').classList.add('hidden');

            CartPerformance.measureFromEvent("add:user-action", evt);
          });
      }

      handleErrorMessage(errorMessage = false) {
        if (this.hideErrors) return;

        this.errorMessageWrapper =
          this.errorMessageWrapper || this.querySelector('.product-form__error-message-wrapper');
        if (!this.errorMessageWrapper) return;
        this.errorMessage = this.errorMessage || this.errorMessageWrapper.querySelector('.product-form__error-message');

        this.errorMessageWrapper.toggleAttribute('hidden', !errorMessage);

        if (errorMessage) {
          this.errorMessage.textContent = errorMessage;
        }
      }

      toggleSubmitButton(disable = true, text) {
        if (disable) {
          this.submitButton.setAttribute('disabled', 'disabled');
          if (text) this.submitButtonText.textContent = text;
        } else {
          this.submitButton.removeAttribute('disabled');
          this.submitButtonText.textContent = window.variantStrings.addToCart;
        }
      }

      get variantIdInput() {
        return this.form.querySelector('[name=id]');
      }
    }
  );
}
